import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import {
  inventoryOptionLabel,
  inventoryPickerOptions,
  inventorySerialOptions,
  inventoryBatchOptions,
  buildIssuanceUnitDetailsFromInventory,
  getInventoryAircraftLabel
} from '../../utils/partMasterLabel';
import { STORE_TYPES, normalizeStoreTypeValue } from '../../constants/storeTypes';
import {
  canApprovePendingIssuance,
  isIssuancePendingApproval,
  isIssuanceRejected,
  isIssuanceReviewable,
  requiresCorrectionNote
} from '../../utils/issuancePermissions';
import {
  isQuantityDraftInput,
  parseQuantityFloat,
  unitRowCountForQuantity
} from '../../utils/quantityFloat';
import { sanitizeUnitDetailsForApiSubmit } from '../../utils/inventoryItemForm';

function issuanceRecordApi(issuance) {
  return String(issuance?.inventoryDomain || '').toUpperCase() === 'LOAN'
    ? (path) => `/api/store/loan${path}`
    : (path) => `/api/store${path}`;
}

function buildInitialForm(issuance) {
  const isLoan = issuance?.loanDirection === 'LOAN_OUT';
  return {
    workOrderId: issuance?.workOrderId?._id || issuance?.workOrderId || '',
    aircraftId: issuance?.aircraftId?._id || issuance?.aircraftId || '',
    recipientType: issuance?.issuedToExternalName ? 'EXTERNAL' : 'USER',
    issuedTo: issuance?.issuedTo?._id || issuance?.issuedTo || '',
    issuedToExternalName: issuance?.issuedToExternalName || '',
    issuedToExternalCompany: issuance?.issuedToExternalCompany || '',
    issuedToExternalPhone: issuance?.issuedToExternalPhone || '',
    deliveryNote: issuance?.deliveryNote || '',
    operatorId: issuance?.operatorId?._id || issuance?.operatorId || '',
    issuedFromAddressId: issuance?.issuedFromAddressId?._id || issuance?.issuedFromAddressId || '',
    returnDueDate: issuance?.returnDueDate ? String(issuance.returnDueDate).slice(0, 10) : '',
    counterpartyCompany: issuance?.counterpartyCompany || '',
    counterpartyContactName: issuance?.counterpartyContactName || '',
    counterpartyContactPhone: issuance?.counterpartyContactPhone || '',
    loanReferenceNumber: issuance?.loanReferenceNumber || '',
    isLoan
  };
}

function buildInitialItems(issuance) {
  return (issuance?.items || []).map((item) => {
    const inv = item?.inventoryItemId;
    const invId = inv?._id || inv || '';
    return {
      inventoryItemId: invId,
      partNumber: item.partNumber || inv?.partNumber || '',
      quantity: item.quantity || 1,
      batchNumber: item.batchNumber || (Array.isArray(item?.unitDetails) ? (item.unitDetails.find((u) => String(u?.batchNumber || '').trim())?.batchNumber || '') : ''),
      serialNumbers: Array.isArray(item.serialNumbers) ? item.serialNumbers : [],
      storeType: item.storeType || '',
      locationText: item.locationText || '',
      remarks: item.remarks || '',
      unitDetails: Array.isArray(item.unitDetails) ? item.unitDetails : [],
      partSearch: item.partNumber || inv?.partNumber || '',
      quantityReturned: Number(item.quantityReturned) || 0
    };
  });
}

const IssuanceReviewDialog = ({
  open,
  issuance,
  inventory,
  workOrders,
  formatWorkOrderOptionLabel,
  aircraftList = [],
  users,
  userRole,
  currentUser,
  onClose,
  onSaved
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(() => buildInitialForm(issuance));
  const [items, setItems] = useState(() => buildInitialItems(issuance));
  const [correctionNote, setCorrectionNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [operators, setOperators] = useState([]);
  const [issuedFromAddresses, setIssuedFromAddresses] = useState([]);

  useEffect(() => {
    if (!open || !issuance) return;
    setForm(buildInitialForm(issuance));
    setItems(buildInitialItems(issuance));
    setQuantityDrafts({});
    setCorrectionNote('');
    setRejectReason('');
    setShowReject(false);
    Promise.all([
      axios.get('/api/master-data/operators', { params: { isActive: 'true' } }),
      axios.get('/api/store/issued-from-addresses', { params: { isActive: 'true' } })
    ]).then(([opRes, addrRes]) => {
      setOperators(opRes.data || []);
      setIssuedFromAddresses(addrRes.data || []);
    }).catch((error) => {
      console.error('Error loading challan context options:', error);
    });
  }, [open, issuance]);

  const inventoryById = useMemo(
    () => new Map(inventory.map((inv) => [String(inv._id), inv])),
    [inventory]
  );

  const pending = isIssuancePendingApproval(issuance);
  const rejected = isIssuanceRejected(issuance);
  const reviewMode = isIssuanceReviewable(issuance);
  const needsNote = requiresCorrectionNote(issuance);
  const canApprove = canApprovePendingIssuance(
    userRole,
    issuance,
    currentUser || { role: userRole }
  );
  const hasReturns = (issuance?.items || []).some((line) => Number(line.quantityReturned) > 0);
  const canAddRemoveItems = reviewMode || !hasReturns;

  const buildPayload = () => {
    const resolvedItems = items.map((item, index) => {
      const draft = quantityDrafts[index];
      if (draft === undefined) return item;
      const minQty = Number(item.quantityReturned) || 0;
      const qty = parseQuantityFloat(draft, {
        min: minQty > 0 ? minQty : 0.001,
        fallback: minQty > 0 ? minQty : 0.001
      });
      return { ...item, quantity: qty };
    });
    const payload = {
      deliveryNote: form.deliveryNote || undefined,
      returnDueDate: form.returnDueDate || undefined,
      operatorId: form.operatorId || undefined,
      issuedFromAddressId: form.issuedFromAddressId || undefined,
      items: resolvedItems.map((item) => ({
        inventoryItemId: item.inventoryItemId,
        quantity: item.quantity,
        serialNumbers: Array.isArray(item.serialNumbers) ? item.serialNumbers : [],
        storeType: item.storeType || undefined,
        locationText: item.locationText || undefined,
        remarks: String(item.remarks || '').trim() || undefined,
        unitDetails: sanitizeUnitDetailsForApiSubmit(
          Array.isArray(item.unitDetails) && item.unitDetails.length > 0
            ? item.unitDetails
            : (item.batchNumber
              ? Array.from({ length: unitRowCountForQuantity(item.quantity) || 1 }, (_, idx) => ({
                  condition: (Array.isArray(item.unitDetails) ? item.unitDetails[idx]?.condition : null) || 'SERVICEABLE',
                  batchNumber: item.batchNumber,
                  serialNumber: ''
                }))
              : [])
        )
      }))
    };
    if (needsNote) payload.correctionNote = correctionNote.trim();
    if (form.isLoan) {
      payload.counterpartyCompany = String(form.counterpartyCompany || '').trim();
      payload.counterpartyContactName = String(form.counterpartyContactName || '').trim() || undefined;
      payload.counterpartyContactPhone = String(form.counterpartyContactPhone || '').trim() || undefined;
      payload.loanReferenceNumber = String(form.loanReferenceNumber || '').trim() || undefined;
    } else {
      payload.workOrderId = form.workOrderId || '';
      payload.aircraftId = form.aircraftId || '';
      if (form.recipientType === 'USER') {
        payload.issuedTo = form.issuedTo;
      } else {
        payload.issuedToExternalName = String(form.issuedToExternalName || '').trim();
        payload.issuedToExternalCompany = String(form.issuedToExternalCompany || '').trim() || undefined;
        payload.issuedToExternalPhone = String(form.issuedToExternalPhone || '').trim() || undefined;
      }
    }
    return payload;
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const getReviewQuantityDisplay = (index, item) => {
    if (quantityDrafts[index] !== undefined) return quantityDrafts[index];
    if (item?.quantity != null) return String(item.quantity);
    return '1';
  };

  const handleReviewQuantityChange = (index, rawValue) => {
    if (!isQuantityDraftInput(rawValue)) return;
    setQuantityDrafts((prev) => ({ ...prev, [index]: rawValue }));
  };

  const commitReviewQuantity = (index) => {
    const draft = quantityDrafts[index];
    if (draft === undefined) return;
    const item = items[index];
    const minQty = Number(item?.quantityReturned) || 0;
    const qty = parseQuantityFloat(draft, {
      min: minQty > 0 ? minQty : 0.001,
      fallback: minQty > 0 ? minQty : 0.001
    });
    setItems((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      const serialCap = Number.isInteger(qty) ? qty : Math.max(1, Math.trunc(qty) || 1);
      const serials = Array.isArray(row.serialNumbers) ? row.serialNumbers.slice(0, serialCap) : [];
      const currentRows = Array.isArray(row.unitDetails) ? row.unitDetails : [];
      const rowCount = unitRowCountForQuantity(qty);
      return {
        ...row,
        quantity: qty,
        serialNumbers: serials,
        unitDetails: Array.from({ length: rowCount }, (_, unitIndex) => (
          currentRows[unitIndex] || {
            condition: 'SERVICEABLE',
            batchNumber: row.batchNumber || '',
            serialNumber: ''
          }
        ))
      };
    }));
    setQuantityDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        inventoryItemId: '',
        partNumber: '',
        quantity: 1,
        batchNumber: '',
        serialNumbers: [],
        storeType: '',
        locationText: '',
        remarks: '',
        unitDetails: [],
        partSearch: '',
        quantityReturned: 0
      }
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setQuantityDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      const remapped = {};
      Object.keys(next).forEach((key) => {
        const k = Number(key);
        if (k > index) remapped[k - 1] = next[key];
        else remapped[key] = next[key];
      });
      return remapped;
    });
  };

  const handleSave = async () => {
    if (!issuance) return;
    if (needsNote && correctionNote.trim().length < 10) {
      showToast('Correction note must be at least 10 characters', 'error');
      return;
    }
    if (form.isLoan && !String(form.counterpartyCompany || '').trim()) {
      showToast('Recipient company is required', 'error');
      return;
    }
    if (!form.isLoan) {
      if (form.recipientType === 'USER' && !form.issuedTo) {
        showToast('Select an MRO user recipient', 'error');
        return;
      }
      if (form.recipientType === 'EXTERNAL' && !String(form.issuedToExternalName || '').trim()) {
        showToast('Enter non-user recipient name', 'error');
        return;
      }
    }
    if (items.length === 0) {
      showToast('At least one item is required', 'error');
      return;
    }
    if (items.some((item) => !item.inventoryItemId)) {
      showToast('Every line must have a part selected', 'error');
      return;
    }

    try {
      setBusy(true);
      const api = issuanceRecordApi(issuance);
      await axios.put(api(`/issuances/${issuance._id}`), buildPayload());
      showToast(
        rejected ? 'Issuance revised and resubmitted for review' : (pending ? 'Issuance updated for review' : 'Issuance corrected successfully'),
        'success'
      );
      onSaved?.();
      onClose?.();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save correction', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!issuance || !canApprove) return;
    try {
      setBusy(true);
      const api = issuanceRecordApi(issuance);
      const { data: updated } = await axios.put(api(`/issuances/${issuance._id}`), buildPayload());
      if (updated.loanDirection === 'LOAN_OUT') {
        if (updated.approvalStatus === 'PENDING_STORE_APPROVAL' && userRole === ROLES.STORE_INSPECTOR) {
          await axios.post(`/api/store/loan/issue/${issuance._id}/submit-approval`);
        } else {
          await axios.post(`/api/store/loan/issue/${issuance._id}/approve`, {
            approvalMode: 'FORMAL',
            approvalNote: correctionNote.trim() || undefined
          });
        }
      } else {
        await axios.post(`/api/store/issuances/${issuance._id}/approve-issue`);
      }
      showToast('Issuance approved', 'success');
      onSaved?.();
      onClose?.();
    } catch (error) {
      showToast(error.response?.data?.message || 'Approval failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!issuance) return;
    try {
      setBusy(true);
      const reason = rejectReason.trim() || 'Rejected during review';
      if (issuance.loanDirection === 'LOAN_OUT') {
        await axios.post(`/api/store/loan/issue/${issuance._id}/reject`, { reason });
      } else {
        await axios.post(`/api/store/issuances/${issuance._id}/reject-issue`, { reason });
      }
      showToast('Issuance rejected', 'success');
      onSaved?.();
      onClose?.();
    } catch (error) {
      showToast(error.response?.data?.message || 'Reject failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!issuance) return null;

  const reviewDialogTitle = reviewMode
    ? (rejected
      ? (form.isLoan ? 'Revise rejected loan-out' : 'Revise rejected issuance')
      : (form.isLoan ? 'Review loan-out' : 'Review issuance'))
    : (form.isLoan ? 'Correct loan-out' : 'Correct issuance');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{reviewDialogTitle}</DialogTitle>
      <DialogContent dividers sx={{ overflowX: 'auto' }}>
        {reviewMode && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {form.isLoan
              ? 'Adjust loan-out lines, recipient details, and challan context as needed, then approve to remove stock from store inventory—or reject to send back to the submitter.'
              : 'Adjust issued quantities, recipient, and line details as needed, then approve to issue stock—or reject to send back to the submitter.'}
            {' '}
            Remove incorrect lines or add omitted parts before approving.
          </Typography>
        )}
        {pending && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This issuance is awaiting review. Correct any mistakes, then approve or reject.
          </Alert>
        )}
        {rejected && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This issuance was rejected{issuance.amRejectionReason ? `: ${issuance.amRejectionReason}` : ''}.
            Update the details and save to resubmit for review.
          </Alert>
        )}
        {!reviewMode && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Inventory has already been updated for this issuance. Describe what you are correcting.
          </Alert>
        )}

        <Grid container spacing={2}>
          {form.isLoan ? (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Recipient company"
                  value={form.counterpartyCompany}
                  onChange={(e) => setForm((prev) => ({ ...prev, counterpartyCompany: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Recipient contact"
                  value={form.counterpartyContactName}
                  onChange={(e) => setForm((prev) => ({ ...prev, counterpartyContactName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Recipient phone"
                  value={form.counterpartyContactPhone}
                  onChange={(e) => setForm((prev) => ({ ...prev, counterpartyContactPhone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Loan reference"
                  value={form.loanReferenceNumber}
                  onChange={(e) => setForm((prev) => ({ ...prev, loanReferenceNumber: e.target.value }))}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Work order</InputLabel>
                  <Select
                    value={form.workOrderId}
                    label="Work order"
                    onChange={(e) => {
                      const workOrderId = e.target.value;
                      const selected = workOrders.find((wo) => String(wo._id) === String(workOrderId));
                      const woAircraftId = selected?.aircraftId?._id || selected?.aircraftId || '';
                      setForm((prev) => ({
                        ...prev,
                        workOrderId,
                        ...(woAircraftId ? { aircraftId: String(woAircraftId) } : {})
                      }));
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {workOrders.map((wo) => (
                      <MenuItem key={wo._id} value={wo._id}>
                        {formatWorkOrderOptionLabel
                          ? formatWorkOrderOptionLabel(wo)
                          : `${wo.title} - ${wo.aircraftId?.registration || 'N/A'}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Aircraft</InputLabel>
                  <Select
                    value={form.aircraftId}
                    label="Aircraft"
                    onChange={(e) => setForm((prev) => ({ ...prev, aircraftId: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {aircraftList.map((ac) => (
                      <MenuItem key={ac._id} value={ac._id}>
                        {ac.registration}{ac.model ? ` — ${ac.model}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Issue to</FormLabel>
                  <RadioGroup
                    row
                    value={form.recipientType}
                    onChange={(e) => setForm((prev) => ({ ...prev, recipientType: e.target.value }))}
                  >
                    <FormControlLabel value="USER" control={<Radio size="small" />} label="MRO user" />
                    <FormControlLabel value="EXTERNAL" control={<Radio size="small" />} label="Non-user" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {form.recipientType === 'USER' ? (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>MRO user</InputLabel>
                    <Select
                      value={form.issuedTo}
                      label="MRO user"
                      onChange={(e) => setForm((prev) => ({ ...prev, issuedTo: e.target.value }))}
                    >
                      {users.map((u) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.firstName} {u.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Recipient name"
                      value={form.issuedToExternalName}
                      onChange={(e) => setForm((prev) => ({ ...prev, issuedToExternalName: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={form.issuedToExternalCompany}
                      onChange={(e) => setForm((prev) => ({ ...prev, issuedToExternalCompany: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={form.issuedToExternalPhone}
                      onChange={(e) => setForm((prev) => ({ ...prev, issuedToExternalPhone: e.target.value }))}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Operator</InputLabel>
              <Select
                value={form.operatorId}
                label="Operator"
                onChange={(e) => setForm((prev) => ({ ...prev, operatorId: e.target.value }))}
              >
                {operators.map((operator) => (
                  <MenuItem key={operator._id} value={operator._id}>
                    {operator.name} ({operator.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Issued From</InputLabel>
              <Select
                value={form.issuedFromAddressId}
                label="Issued From"
                onChange={(e) => setForm((prev) => ({ ...prev, issuedFromAddressId: e.target.value }))}
              >
                {issuedFromAddresses.map((addr) => (
                  <MenuItem key={addr._id} value={addr._id}>
                    {addr.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              label="Return due date"
              value={form.returnDueDate}
              onChange={(e) => setForm((prev) => ({ ...prev, returnDueDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Delivery note"
              value={form.deliveryNote}
              onChange={(e) => setForm((prev) => ({ ...prev, deliveryNote: e.target.value }))}
            />
          </Grid>
          {needsNote && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                label="Correction note"
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
                helperText="Required — minimum 10 characters"
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">Items</Typography>
          {canAddRemoveItems && (
            <Button size="small" startIcon={<Add />} onClick={handleAddItem}>
              Add item
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table
          size="small"
          sx={{
            mt: 1,
            minWidth: 1200,
            tableLayout: 'auto',
            '& .MuiTableCell-root': { verticalAlign: 'top' },
            '& .MuiInputBase-root': { fontSize: '0.875rem' }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 160 }}>Part</TableCell>
              <TableCell sx={{ minWidth: 100 }}>Stock Aircraft</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Description</TableCell>
              <TableCell sx={{ minWidth: 96 }}>Qty</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Store</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Location</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Batch</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Serials</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Remarks</TableCell>
              {canAddRemoveItems && <TableCell sx={{ minWidth: 72 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const inventoryItem = inventoryById.get(String(item.inventoryItemId));
              const batchOptions = inventoryBatchOptions(inventoryItem);
              const pickerOptions = inventoryPickerOptions(inventory, item.partSearch || '');
              const minQty = Number(item.quantityReturned) || 0;
              return (
                <TableRow key={index}>
                  <TableCell sx={{ minWidth: 220 }}>
                    {canAddRemoveItems && !minQty ? (
                      <Autocomplete
                        size="small"
                        options={pickerOptions}
                        value={inventoryItem || null}
                        inputValue={item.partSearch || ''}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'clear') {
                            setItems((prev) => prev.map((row, i) => (i === index ? {
                              ...row,
                              partSearch: '',
                              inventoryItemId: '',
                              partNumber: '',
                              batchNumber: '',
                              serialNumbers: [],
                              unitDetails: []
                            } : row)));
                            return;
                          }
                          // Ignore Autocomplete "reset" so inventory labels never overwrite search text.
                          if (reason !== 'input') return;
                          handleItemChange(index, 'partSearch', value);
                          const selected = inventoryById.get(String(item.inventoryItemId));
                          if (selected && value !== String(selected.partNumber || '')) {
                            handleItemChange(index, 'inventoryItemId', '');
                            handleItemChange(index, 'partNumber', '');
                            handleItemChange(index, 'batchNumber', '');
                            handleItemChange(index, 'serialNumbers', []);
                            handleItemChange(index, 'unitDetails', []);
                          }
                        }}
                        onChange={(_, selected) => {
                          if (!selected) {
                            handleItemChange(index, 'inventoryItemId', '');
                            handleItemChange(index, 'partNumber', '');
                            handleItemChange(index, 'batchNumber', '');
                            handleItemChange(index, 'serialNumbers', []);
                            handleItemChange(index, 'unitDetails', []);
                            return;
                          }
                          setItems((prev) => prev.map((row, i) => (i === index ? {
                            ...row,
                            inventoryItemId: selected._id,
                            partNumber: selected.partNumber,
                            partSearch: selected.partNumber || '',
                            serialNumbers: [],
                            batchNumber: '',
                            storeType: selected.storeType || '',
                            locationText: selected.locationText || '',
                            unitDetails: []
                          } : row)));
                        }}
                        filterOptions={(opts) => opts}
                        getOptionLabel={(option) => String(option?.partNumber || '')}
                        isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            {inventoryOptionLabel(option)}
                          </li>
                        )}
                        renderInput={(params) => <TextField {...params} placeholder="Search part" />}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {item.partNumber || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {inventoryItem ? (getInventoryAircraftLabel(inventoryItem) || '—') : '—'}
                  </TableCell>
                  <TableCell>{inventoryItem?.description || '—'}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={getReviewQuantityDisplay(index, item)}
                      onChange={(e) => handleReviewQuantityChange(index, e.target.value)}
                      onBlur={() => commitReviewQuantity(index)}
                      inputProps={{ min: minQty || 0.001, step: 'any' }}
                      helperText={minQty > 0 ? `${minQty} returned` : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      displayEmpty
                      value={normalizeStoreTypeValue(item.storeType)}
                      onChange={(e) => handleItemChange(index, 'storeType', e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value=""><em>Select</em></MenuItem>
                      {STORE_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.locationText || ''}
                      onChange={(e) => handleItemChange(index, 'locationText', e.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <TextField
                      size="small"
                      disabled={!inventoryItem}
                      value={item.batchNumber || ''}
                      onChange={(e) => {
                        const selectedBatch = String(e.target.value || '').trim();
                        const qty = Number(item.quantity) || 0;
                        const rowCount = unitRowCountForQuantity(qty) || 1;
                        const nextRows = Array.from({ length: rowCount }, (_, unitIndex) => ({
                          ...(Array.isArray(item.unitDetails) ? item.unitDetails[unitIndex] : null) || { condition: 'SERVICEABLE', serialNumber: '' },
                          batchNumber: selectedBatch
                        }));
                        handleItemChange(index, 'batchNumber', selectedBatch);
                        handleItemChange(index, 'unitDetails', nextRows);
                      }}
                      inputProps={{ list: `review-issuance-batch-options-${index}` }}
                      placeholder={inventoryItem ? 'Select batch' : 'Select part first'}
                    />
                    <datalist id={`review-issuance-batch-options-${index}`}>
                      {batchOptions.map((batch) => (
                        <option key={batch} value={batch} />
                      ))}
                    </datalist>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      disabled={!inventoryItem}
                      options={inventorySerialOptions(inventoryItem)}
                      value={Array.isArray(item.serialNumbers) ? item.serialNumbers : []}
                      onChange={(_, selected) => {
                        const normalized = Array.from(
                          new Set((selected || []).map((serial) => String(serial || '').trim()).filter(Boolean))
                        );
                        const nextSerials = normalized.slice(0, (() => {
                          const qty = Number(item.quantity) || 0;
                          return Number.isInteger(qty) ? qty : Math.max(1, Math.trunc(qty) || 1);
                        })() || 1);
                        handleItemChange(index, 'serialNumbers', nextSerials);
                        const details = buildIssuanceUnitDetailsFromInventory(inventoryItem, nextSerials);
                        handleItemChange(index, 'unitDetails', details);
                        const uniqueBatches = [...new Set(details.map((row) => String(row?.batchNumber || '').trim()).filter(Boolean))];
                        handleItemChange(index, 'batchNumber', uniqueBatches.length === 1 ? uniqueBatches[0] : '');
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="Serials" />}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.remarks || ''}
                      onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                      placeholder="Remarks"
                    />
                  </TableCell>
                  {canAddRemoveItems && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(index)}
                        disabled={minQty > 0}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>

        {showReject && (
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            multiline
            minRows={2}
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {pending && (
          <>
            {!showReject ? (
              <Button color="error" onClick={() => setShowReject(true)} disabled={busy}>
                Reject
              </Button>
            ) : (
              <Button color="error" variant="contained" onClick={handleReject} disabled={busy}>
                Confirm reject
              </Button>
            )}
          </>
        )}
        <Button variant="outlined" onClick={handleSave} disabled={busy}>
          {reviewMode ? (rejected ? 'Resubmit for review' : 'Save changes') : 'Save correction'}
        </Button>
        {reviewMode && canApprove && (
          <Button variant="contained" onClick={handleApprove} disabled={busy}>
            Approve & issue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IssuanceReviewDialog;
